import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Circle, X } from "lucide-react";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";

type MeResponse = {
  professional?: { slug?: string; onboardingBannerDismissed?: boolean };
  accountType?: "PROFESSIONAL" | "FACILITY";
  mpConnected?: boolean;
};

const actionLink = css({
  fontSize: "xs",
  fontWeight: "semibold",
  color: "token(colors.brand.700)",
  bg: "rgba(99, 102, 241, 0.12)",
  border: "none",
  rounded: "md",
  px: "3",
  py: "1.5",
  cursor: "pointer",
  whiteSpace: "nowrap",
  textDecoration: "none",
});

export function OnboardingChecklistBanner() {
  const { refresh } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [accountType, setAccountType] = useState<"PROFESSIONAL" | "FACILITY">("PROFESSIONAL");
  const [mpConnected, setMpConnected] = useState(false);
  const [slug, setSlug] = useState("");
  const [offeringCount, setOfferingCount] = useState<number | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const rMe = await fetchWithSessionSync("/api/dashboard/me", { credentials: "include" }, refresh);
      if (cancelled) return;
      if (!rMe.ok) {
        setLoading(false);
        return;
      }
      const me = (await rMe.json()) as MeResponse;
      const at = me.accountType ?? "PROFESSIONAL";
      setAccountType(at);
      setMpConnected(Boolean(me.mpConnected));
      setSlug(me.professional?.slug ?? "");
      setDismissed(Boolean(me.professional?.onboardingBannerDismissed));

      const offeringUrl = at === "FACILITY" ? "/api/dashboard/courts" : "/api/dashboard/services";
      const rOff = await fetchWithSessionSync(offeringUrl, { credentials: "include" }, refresh);
      if (!cancelled) {
        if (rOff.ok) {
          const j = (await rOff.json()) as unknown[] | { courts: unknown[] };
          const list = Array.isArray(j) ? j : (j.courts ?? []);
          setOfferingCount(list.length);
        } else {
          setOfferingCount(0);
        }
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  if (loading || dismissed) return null;

  const hasOffering = (offeringCount ?? 0) > 0;
  if (hasOffering && mpConnected) return null;

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const publicUrl = `${origin}/book/${slug}`;

  function dismiss() {
    setDismissed(true);
    void fetchWithSessionSync(
      "/api/dashboard/me",
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ onboardingBannerDismissed: true }),
      },
      refresh,
    );
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setLinkCopied(true);
    } catch {
      setLinkCopied(false);
    }
  }

  const items: { key: string; label: string; done: boolean; action: React.ReactNode }[] = [
    {
      key: "offering",
      label: accountType === "FACILITY" ? "Cargá tu primera cancha" : "Cargá tu primer servicio",
      done: hasOffering,
      action: (
        <Link to={accountType === "FACILITY" ? "/dashboard/courts" : "/dashboard/services"} className={actionLink}>
          {accountType === "FACILITY" ? "Ir a Canchas" : "Ir a Servicios"}
        </Link>
      ),
    },
    {
      key: "payment",
      label: "Configurá tu método de cobro",
      done: mpConnected,
      action: (
        <Link to="/dashboard/payments" className={actionLink}>
          Configurar cobro
        </Link>
      ),
    },
    {
      key: "share",
      label: "Compartí tu link de reservas",
      done: linkCopied,
      action: (
        <button type="button" onClick={() => void copyLink()} className={actionLink}>
          {linkCopied ? "Copiado" : "Copiar link"}
        </button>
      ),
    },
  ];

  return (
    <div className={cx(cf.dashSurface, css({ p: "5", mb: "5" }))}>
      <div className={css({ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "4" })}>
        <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a" })}>Primeros pasos</p>
        <button
          type="button"
          aria-label="Cerrar"
          onClick={dismiss}
          className={css({
            bg: "transparent",
            border: "none",
            cursor: "pointer",
            color: "#94a3b8",
            display: "flex",
            p: "1",
            _hover: { color: "#64748b" },
          })}
        >
          <X size={16} strokeWidth={1.75} />
        </button>
      </div>
      <ul className={css({ display: "flex", flexDir: "column", gap: "3" })}>
        {items.map((item) => (
          <li
            key={item.key}
            className={css({
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "3",
              flexWrap: "wrap",
            })}
          >
            <span className={css({ display: "flex", alignItems: "center", gap: "2.5" })}>
              {item.done ? (
                <Check className={css({ w: "4.5", h: "4.5", color: "token(colors.brand.500)", flexShrink: 0 })} />
              ) : (
                <Circle className={css({ w: "4.5", h: "4.5", color: "#cbd5e1", flexShrink: 0 })} />
              )}
              <span
                className={css({
                  fontSize: "sm",
                  color: item.done ? "#94a3b8" : "#334155",
                  textDecoration: item.done ? "line-through" : "none",
                })}
              >
                {item.label}
              </span>
            </span>
            {!item.done ? item.action : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
